import {
  createContext,
  type MouseEvent,
  type ReactNode,
  useContext,
} from "react";

import { useMediaQuery } from "usehooks-ts";

import { BuiFloatingActionBar } from "@btnet/blueprint-components";
import { IconCaretSmallUp, IconX } from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import { BTButton, type IBTButtonPropsBase } from "../BTButton/BTButton";
import { BTDropdown } from "../BTDropdown/BTDropdown";
import { BTMenu } from "../BTMenu/BTMenu";
import { BTPopover } from "../BTPopover/BTPopover";
import "./BTFloatingActionBar.less";

/**
 * Click handler shared by every FAB action and menu item. May be async — the bar
 * fires the action and does not await the result.
 */
export type TBTFloatingActionBarClickHandler = (
  event?: MouseEvent<HTMLElement>,
) => void | Promise<void>;

/**
 * The shared shape of a FAB action ({@link IBTFloatingActionBarActionProps},
 * {@link IBTFloatingActionBarIconActionProps}). It inherits `BTButton`'s props
 * (variant, disabled, etc.), minus the ones the bar owns itself — the testid
 * (derived from `id`), the icon/label content, the className, and the responsive
 * collapse.
 */
interface IBTFloatingActionBarActionBase
  extends Omit<
    IBTButtonPropsBase,
    "data-testid" | "children" | "icon" | "className" | "collapse" | "onClick"
  > {
  /**
   * Stable unique id for the action. Used as the React key and to derive the
   * action's testid, so it must be unique within a single bar.
   */
  id: string;

  /**
   * The action's label. Rendered inline (as button text, or as the tooltip +
   * accessible name for an icon-only action) and as the menu-item text when the
   * bar collapses.
   */
  children: ReactNode;

  /** Click handler invoked when the action is triggered (inline or collapsed). */
  onClick: TBTFloatingActionBarClickHandler;

  /**
   * When true, the action renders in the danger (red) style — both inline and in
   * its collapsed menu item.
   * @default false
   */
  danger?: boolean;
}

/**
 * Props for {@link BTFloatingActionBar} — the app-facing, list-page floating
 * action bar. The root shows automatically while `selectedCount > 0` and always
 * renders the built-in leading group — the deselect (X) button, the
 * "{count} selected" label, and a divider — mirroring the legacy
 * `BdsFloatingActionBar`. The actions are composed from the sub-components
 * ({@link BTFloatingActionBar.IconAction}, {@link BTFloatingActionBar.Action},
 * {@link BTFloatingActionBar.Divider}, {@link BTFloatingActionBar.MoreActions}).
 *
 * At/above the `md` breakpoint the inline actions and the optional `MoreActions`
 * dropdown are shown; below `md` every action merges into a single, text-only
 * combined "Actions" dropdown.
 */
export interface IBTFloatingActionBarProps {
  /**
   * Number of currently-selected items. The bar is shown (with its slide/fade
   * animation) while this is greater than zero and hidden once it reaches zero.
   * Also drives the built-in "{count} selected" label.
   */
  selectedCount: number;

  /**
   * Callback triggered when the user clicks the built-in deselect (X) button.
   * Consumers should clear their selection in response.
   */
  onDeselect: () => void;

  /**
   * The bar's actions — compose them from the `BTFloatingActionBar.*`
   * sub-components (`IconAction`, `Action`, `Divider`, `MoreActions`). The
   * leading deselect + count label are built in and must not be passed here.
   */
  children: ReactNode;

  /**
   * Accessible label for the bar's toolbar region. Defaults to `"Grid actions"`.
   * @default "Grid actions"
   */
  ariaLabel?: string;

  /**
   * Additional class name(s) to apply to the bar.
   */
  className?: string;

  /**
   * The required data-testid so it can be granular for testing. Sub-components
   * derive their own testids from this (e.g. `"{testid}-deselect"`,
   * `"{testid}-{actionId}"`).
   */
  testid: string;
}

/**
 * Props for {@link BTFloatingActionBar.Action} — a text bulk action with an
 * optional leading icon. Inline (at/above `md`) it renders as a text button
 * ("{icon} {label}"); when the bar collapses below `md` it becomes a text-only
 * menu item in the combined "Actions" dropdown (the icon is dropped). Use this
 * for the higher-priority, always-labeled actions (e.g. "Send", "Move",
 * "Mark ready for payment"). For icon-only inline actions, use
 * {@link BTFloatingActionBar.IconAction}.
 */
export interface IBTFloatingActionBarActionProps
  extends IBTFloatingActionBarActionBase {
  /**
   * Optional leading icon shown before the label inline. It is dropped when the
   * bar collapses (the dropdown is text-only).
   */
  icon?: ReactNode;
}

/**
 * Props for {@link BTFloatingActionBar.IconAction} — an icon-only bulk action.
 * Inline (at/above `md`) it renders icon-only, with the label (`children`)
 * surfaced as a hover/focus tooltip and used as the accessible name; when the
 * bar collapses below `md` it becomes a text-only menu item in the combined
 * "Actions" dropdown. Use this for the compact, icon-only actions (e.g. Print,
 * Copy, Delete). For always-labeled actions, use
 * {@link BTFloatingActionBar.Action}.
 */
export interface IBTFloatingActionBarIconActionProps
  extends IBTFloatingActionBarActionBase {
  /**
   * The icon shown inline. Required — this is the icon-only action.
   */
  icon: ReactNode;
}

/**
 * Props for {@link BTFloatingActionBar.Divider} — a vertical separator between
 * groups of inline actions. Stays inline and is dropped when the bar collapses.
 */
export interface IBTFloatingActionBarDividerProps {
  /**
   * Additional class name(s) to apply to the divider rule.
   */
  className?: string;
}

/**
 * Props for {@link BTFloatingActionBar.MoreActions} — the explicit overflow
 * dropdown shown at the end of the bar (mirroring the legacy
 * BdsFloatingActionBar's "More actions" menu). Compose it from
 * {@link BTFloatingActionBar.MoreActionsItem} children. While the bar is
 * expanded it renders its own trigger + menu.
 *
 * The expanded trigger is a button showing `label` (default "More actions")
 * followed by an upward caret. Three knobs adjust it:
 * - **`icon`:** a leading icon, rendered through the button's `icon` prop (like
 *   the inline {@link BTFloatingActionBar.IconAction}s). Pass `icon` with no
 *   `label` for an icon-only trigger; pair it with `ariaLabel` for the name.
 * - **`label` as a `ReactNode`:** custom trigger content (usually text).
 * - **`caret={false}`:** drop the trailing caret so an icon-only trigger sits
 *   flush with the surrounding icon actions.
 *
 * When the bar collapses below the `md` breakpoint, behavior depends on `label`
 * (the `icon` is dropped — the collapsed menu is text-only):
 * - **Default (no `label`):** the items flatten into the single combined
 *   "Actions" dropdown alongside the inline actions.
 * - **Custom label (e.g. "Attach to"):** the group is preserved as a labeled
 *   submenu inside the combined "Actions" dropdown, matching the legacy
 *   BdsFloatingActionBar.
 *
 * A bar may contain more than one `MoreActions` group (e.g. a "New" dropdown
 * next to the "More actions" overflow). When it does, give each group a unique
 * `id` so their triggers/menus get distinct testids and don't collide.
 */
export interface IBTFloatingActionBarMoreActionsProps {
  /**
   * The menu contents — a set of {@link BTFloatingActionBar.MoreActionsItem}
   * children.
   */
  children: ReactNode;

  /**
   * Unique id for this group, used as the testid suffix for its trigger/menu
   * (`{root}-{id}-trigger`, `{root}-{id}-menu`) and, when collapsed, for its
   * submenu. Required to be unique when a bar has multiple `MoreActions` groups.
   * @default "more"
   */
  id?: string;

  /**
   * The trigger's leading icon, rendered through the button's `icon` prop so it
   * matches the inline {@link BTFloatingActionBar.IconAction}s. Pass `icon` alone
   * (no `label`) for an icon-only trigger — set `ariaLabel` for its accessible
   * name and usually `caret={false}` so it sits flush with the icon actions. The
   * icon is dropped when the bar collapses (the combined dropdown is text-only).
   */
  icon?: ReactNode;

  /**
   * The trigger's text/content while the bar is expanded. Providing a custom
   * label also preserves the group as a labeled submenu when the bar collapses;
   * with no label the group flattens on collapse. Falls back to "More actions"
   * only when no `icon` is given either.
   *
   * A `ReactNode`, but when it isn't plain text set `ariaLabel` so the trigger
   * still has an accessible name.
   * @default "More actions"
   */
  label?: ReactNode;

  /**
   * Accessible name for the trigger button. Supply this whenever the trigger has
   * no plain-text label (e.g. an icon-only trigger), so it is still labeled for
   * assistive tech.
   */
  ariaLabel?: string;

  /**
   * Whether the expanded trigger shows the trailing upward caret. Set `false` for
   * an icon-only trigger so it sits flush with the inline icon actions.
   * @default true
   */
  caret?: boolean;
}

/**
 * Props for {@link BTFloatingActionBar.MoreActionsItem} — a single item inside
 * the {@link BTFloatingActionBar.MoreActions} dropdown.
 */
export interface IBTFloatingActionBarMoreActionsItemProps {
  /**
   * Stable unique id for the item. Used as the React key and to derive the
   * item's testid, so it must be unique within a single bar.
   */
  id: string;

  /**
   * The item's label.
   */
  children: ReactNode;

  /** Click handler invoked when the item is selected. */
  onClick: TBTFloatingActionBarClickHandler;

  /**
   * Optional leading icon for the item.
   */
  icon?: ReactNode;

  /**
   * When true, the item renders in the danger (red) style — e.g. "Void".
   * @default false
   */
  danger?: boolean;

  /**
   * When true, the item renders disabled and its click handler is not called.
   * @default false
   */
  disabled?: boolean;
}

// Lets sub-components read the bar's testid and current collapse state without
// prop-drilling or child cloning. Each sub-component renders itself for the
// active layout — an inline control when expanded, a menu node when collapsed —
// so consumers just compose the tree and the layout resolves through context.
interface IFabContext {
  testid: string;
  isCollapsed: boolean;
  /**
   * The testid prefix for items rendered inside an overflow/combined dropdown
   * menu. Set by {@link OverflowDropdown} to its own testid so a menu item reads
   * as `{menuTestid}-{id}` (e.g. `{root}-more-{id}` in the expanded overflow,
   * `{root}-actions-{id}` in the collapsed combined menu). Undefined for inline
   * actions, which key off the root `testid` instead.
   */
  menuTestid?: string | undefined;
}

const FabContext = createContext<IFabContext>({
  testid: "",
  isCollapsed: false,
});

// The vertical rule between inline groups. mx-2 (8px) on top of the bar's 8px
// gap yields ~16px on each side, matching the legacy BdsFloatingActionBar.
// Shared by the composable `Divider` and the root's built-in leading divider.
const FabDividerRule = ({ className }: { className?: string | undefined }) => (
  <div
    aria-hidden="true"
    className={cn("mx-2 h-8 self-center border-l border-blue-5", className)}
  />
);

// The one menu item shape every action collapses to. `Action`, `IconAction`,
// and `MoreActionsItem` all render this — a text-only item keyed off the action
// `id`, dropping any inline-only affordances (icons, tooltips).
const FabMenuItem = ({
  testid,
  id,
  children,
  onClick,
  icon,
  danger = false,
  disabled = false,
}: {
  testid: string;
  id: string;
  children: ReactNode;
  onClick: TBTFloatingActionBarClickHandler;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
}) => (
  <BTMenu.Item
    testid={`${testid}-${id}`}
    icon={icon}
    danger={danger}
    disabled={disabled}
    // BuiMenu hardcodes the item text color (incl. on :hover), swallowing antd's
    // built-in danger red. Re-assert it with a utility: the Tailwind `utilities`
    // layer beats BuiMenu's app-layer rule in every state, so no :hover override
    // or !important is needed. text-red-70 matches the legacy BdsFloatingActionBar.
    className={cn({ "text-red-70": danger })}
    onClick={() => void onClick()}
  >
    {children}
  </BTMenu.Item>
);

/**
 * A text bulk action with an optional leading icon. Inline (at/above `md`) it
 * renders as a text button ("{icon} {label}"); when the bar collapses below `md`
 * it renders itself as a text-only item in the combined "Actions" dropdown (the
 * icon is dropped). Use for higher-priority, always-labeled actions. For
 * icon-only inline actions, see {@link BTFloatingActionBarIconAction}.
 *
 * @example
 * <BTFloatingActionBar.Action id="send" icon={<IconPaperPlaneRight />} onClick={onSend}>
 *   Send
 * </BTFloatingActionBar.Action>
 */
const BTFloatingActionBarAction = ({
  id,
  children,
  onClick,
  icon,
  type = "tertiary",
  danger = false,
  disabled = false,
  ...buttonProps
}: IBTFloatingActionBarActionProps) => {
  const { testid, isCollapsed, menuTestid } = useContext(FabContext);

  // Collapsed: a text-only menu item in the root's combined dropdown, keyed off
  // that dropdown's testid (`{root}-actions-{id}`).
  if (isCollapsed) {
    return (
      <FabMenuItem
        testid={menuTestid ?? testid}
        id={id}
        onClick={onClick}
        danger={danger}
        disabled={disabled}
      >
        {children}
      </FabMenuItem>
    );
  }

  return (
    <BTButton
      {...buttonProps}
      data-testid={`${testid}-${id}`}
      type={type}
      onClick={(event) => void onClick(event)}
      icon={icon}
      disabled={disabled}
      className={cn({ "BTFloatingActionBar-danger": danger })}
    >
      {children}
    </BTButton>
  );
};

/**
 * An icon-only bulk action. Inline (at/above `md`) it renders icon-only, with
 * the label (`children`) shown as a hover/focus tooltip (via `BTPopover`) and
 * used as the accessible name; when the bar collapses below `md` it renders
 * itself as a text-only item in the combined "Actions" dropdown. Use for
 * compact, icon-only actions. For always-labeled actions, see
 * {@link BTFloatingActionBarAction}.
 *
 * @example
 * <BTFloatingActionBar.IconAction id="delete" icon={<IconTrash />} onClick={onDelete}>
 *   Delete
 * </BTFloatingActionBar.IconAction>
 */
const BTFloatingActionBarIconAction = ({
  id,
  children,
  icon,
  onClick,
  type = "tertiary",
  danger = false,
  disabled = false,
  ...buttonProps
}: IBTFloatingActionBarIconActionProps) => {
  const { testid, isCollapsed, menuTestid } = useContext(FabContext);

  // Collapsed: a text-only menu item (the icon is dropped), keyed off the
  // combined dropdown's testid (`{root}-actions-{id}`).
  if (isCollapsed) {
    return (
      <FabMenuItem
        testid={menuTestid ?? testid}
        id={id}
        onClick={onClick}
        danger={danger}
        disabled={disabled}
      >
        {children}
      </FabMenuItem>
    );
  }

  return (
    <BTPopover content={children} placement="top">
      <BTButton
        {...buttonProps}
        data-testid={`${testid}-${id}`}
        type={type}
        onClick={(event) => void onClick(event)}
        icon={icon}
        disabled={disabled}
        aria-label={typeof children === "string" ? children : undefined}
        className={cn({ "BTFloatingActionBar-danger": danger })}
      />
    </BTPopover>
  );
};

/**
 * A separator between groups of actions. Inline (at/above `md`) it renders a
 * vertical rule between inline groups; when the bar collapses it renders a
 * horizontal `BTMenu.Divider` at the same position in the combined dropdown.
 */
const BTFloatingActionBarDivider = ({
  className,
}: IBTFloatingActionBarDividerProps) => {
  const { isCollapsed } = useContext(FabContext);

  if (isCollapsed) {
    // bg-gray-20 is the border-default color; the Tailwind `utilities` layer
    // beats BuiMenu's app-layer default fill, so no .less override is needed.
    return <BTMenu.Divider className="bg-gray-20" />;
  }

  return <FabDividerRule className={className} />;
};

/**
 * A single item inside a {@link BTFloatingActionBar.MoreActions} group. Renders
 * a `BTMenu.Item` — inside its group's own dropdown while the bar is expanded,
 * or inside the root's combined "Actions" dropdown once it collapses.
 */
const BTFloatingActionBarMoreActionsItem = ({
  id,
  children,
  onClick,
  icon,
  danger = false,
  disabled = false,
}: IBTFloatingActionBarMoreActionsItemProps) => {
  const { testid, menuTestid } = useContext(FabContext);

  return (
    <FabMenuItem
      // Menu items key off the containing dropdown's testid (`{root}-more` when
      // expanded, `{root}-actions` when collapsed) so they read as
      // `{root}-more-{id}` / `{root}-actions-{id}`, falling back to the root.
      testid={menuTestid ?? testid}
      id={id}
      onClick={onClick}
      icon={icon}
      danger={danger}
      disabled={disabled}
    >
      {children}
    </FabMenuItem>
  );
};

/**
 * The dropdown trigger + menu used both for the expanded "More actions" group
 * and the root's collapsed combined "Actions" menu. Its children self-render as
 * `BTMenu.Item`s / `BTMenu.SubMenu`s. The menu opens upward, so the trigger
 * caret points up.
 *
 * The trigger renders an optional leading `icon` (through the button's `icon`
 * prop, matching the inline icon actions), the `triggerLabel` content, and, by
 * default, a trailing upward caret. Set `caret={false}` to drop the caret — e.g.
 * for an icon-only trigger that should sit flush with the surrounding icon
 * actions. Pass `ariaLabel` to name the trigger when it has no text label.
 */
const OverflowDropdown = ({
  testid,
  triggerLabel,
  icon,
  ariaLabel,
  caret = true,
  children,
}: {
  testid: string;
  triggerLabel?: ReactNode;
  icon?: ReactNode;
  ariaLabel?: string | undefined;
  caret?: boolean | undefined;
  children: ReactNode;
}) => {
  const parent = useContext(FabContext);

  return (
    <BTDropdown
      testid={`${testid}-dropdown`}
      placement="topCenter"
      overlay={
        <BTMenu
          testid={`${testid}-menu`}
          aria-label="More options"
          className="BTFloatingActionBar-menu"
        >
          {/* Items inside this menu self-render off `menuTestid`, so they read as
              `{testid}-{id}` (e.g. `{root}-more-{id}` / `{root}-actions-{id}`). */}
          <FabContext.Provider value={{ ...parent, menuTestid: testid }}>
            {children}
          </FabContext.Provider>
        </BTMenu>
      }
      trigger={["click"]}
    >
      <BTButton
        data-testid={`${testid}-trigger`}
        type="tertiary"
        icon={icon}
        aria-label={ariaLabel}
      >
        {(triggerLabel || caret) && (
          <span className="flex items-center gap-1 whitespace-nowrap">
            {triggerLabel}
            {caret && <IconCaretSmallUp />}
          </span>
        )}
      </BTButton>
    </BTDropdown>
  );
};

/**
 * A dropdown menu group in the bar. While the bar is expanded it renders its
 * own trigger + menu ("More actions ▴" by default, or a custom `label` like
 * "New ▴"). Pass an `icon` (rendered through the button's `icon` prop, like the
 * inline icon actions) for an icon-only trigger — with `ariaLabel` for the
 * accessible name and `caret={false}` so it sits flush with the icon actions.
 *
 * Once the bar collapses below `md`, it folds into the root's combined "Actions"
 * dropdown: a default group (no `label`) flattens its items in place, while a
 * custom-labeled group (e.g. "Attach to") is preserved as a labeled submenu.
 *
 * A bar may contain multiple groups — e.g. a "New" dropdown next to the "More
 * actions" overflow. Give each a unique `id` so their testids don't collide.
 *
 * @example
 * <BTFloatingActionBar.MoreActions id="new" label="New">
 *   <BTFloatingActionBar.MoreActionsItem id="bill" onClick={onNewBill}>
 *     Bill
 *   </BTFloatingActionBar.MoreActionsItem>
 * </BTFloatingActionBar.MoreActions>
 * <BTFloatingActionBar.MoreActions
 *   id="export"
 *   icon={<IconDownloadSimple />}
 *   ariaLabel="Export"
 *   caret={false}
 * >
 *   <BTFloatingActionBar.MoreActionsItem id="csv" onClick={onExportCsv}>
 *     CSV
 *   </BTFloatingActionBar.MoreActionsItem>
 * </BTFloatingActionBar.MoreActions>
 * <BTFloatingActionBar.MoreActions>
 *   <BTFloatingActionBar.MoreActionsItem id="void" danger onClick={onVoid}>
 *     Void
 *   </BTFloatingActionBar.MoreActionsItem>
 *   <BTFloatingActionBar.MoreActionsItem id="attach" onClick={onAttach}>
 *     Attach files
 *   </BTFloatingActionBar.MoreActionsItem>
 * </BTFloatingActionBar.MoreActions>
 */
const BTFloatingActionBarMoreActions = ({
  children,
  icon,
  label,
  ariaLabel,
  caret,
  id = "more",
}: IBTFloatingActionBarMoreActionsProps) => {
  const { testid, isCollapsed } = useContext(FabContext);

  if (isCollapsed) {
    // A custom label keeps the group as a labeled submenu in the combined menu;
    // the default (no label) flattens the items in place. The icon is dropped —
    // the collapsed menu is text-only, like every other action.
    if (label === undefined) {
      return <>{children}</>;
    }
    return (
      <BTMenu.SubMenu testid={`${testid}-${id}`} title={label}>
        {children}
      </BTMenu.SubMenu>
    );
  }

  // Expanded: this group's items under its own trigger. `id` keeps the testids
  // distinct when a bar has more than one MoreActions group. Fall back to the
  // "More actions" text only when there's no icon to stand in for it.
  return (
    <OverflowDropdown
      testid={`${testid}-${id}`}
      icon={icon}
      triggerLabel={label ?? (icon ? undefined : "More actions")}
      ariaLabel={ariaLabel}
      caret={caret}
    >
      {children}
    </OverflowDropdown>
  );
};

/**
 * BTFloatingActionBar - the app-facing, list-page floating action bar.
 *
 * A composable compound component mirroring the legacy `BdsFloatingActionBar`:
 * the root owns open/visibility and the responsive collapse, while its content
 * is assembled from the sub-components — `Action`, `IconAction`, `Divider`, and
 * `MoreActions` / `MoreActionsItem`. Each sub-component renders itself for the
 * active layout: at/above the `md` breakpoint the inline `Action`s show on the
 * left and the optional `MoreActions` dropdown on the right; below `md` every
 * action renders as an item inside a single combined "Actions" dropdown, with
 * no loss of any action.
 *
 * @param props - {@link IBTFloatingActionBarProps} controlling selection, actions, and styling.
 * @returns The rendered floating action bar.
 * @example
 * <BTFloatingActionBar
 *   testid="bill-action-bar"
 *   selectedCount={selected.length}
 *   onDeselect={clearSelection}
 * >
 *   <BTFloatingActionBar.IconAction id="print" icon={<IconPrinter />} onClick={onPrint}>
 *     Print
 *   </BTFloatingActionBar.IconAction>
 *   <BTFloatingActionBar.Divider />
 *   <BTFloatingActionBar.Action id="mark-ready" onClick={onMarkReady}>
 *     Mark ready for payment
 *   </BTFloatingActionBar.Action>
 *   <BTFloatingActionBar.MoreActions>
 *     <BTFloatingActionBar.MoreActionsItem id="void" danger onClick={onVoid}>
 *       Void
 *     </BTFloatingActionBar.MoreActionsItem>
 *   </BTFloatingActionBar.MoreActions>
 * </BTFloatingActionBar>
 */

const BTFloatingActionBarInternal = ({
  selectedCount,
  onDeselect,
  children,
  ariaLabel = "Grid actions",
  className,
  testid,
}: IBTFloatingActionBarProps) => {
  const isCollapsed = !useMediaQuery("(min-width: 768px)");

  return (
    <BuiFloatingActionBar
      isOpen={selectedCount > 0}
      ariaLabel={ariaLabel}
      testid={testid}
      className={cn("BTFloatingActionBar gap-2", className)}
    >
      <FabContext.Provider value={{ testid, isCollapsed }}>
        <span className="whitespace-nowrap">{selectedCount} selected</span>
        <BTButton
          data-testid={`${testid}-deselect`}
          type="tertiary"
          aria-label="Uncheck all items"
          onClick={onDeselect}
          icon={<IconX />}
        />
        <FabDividerRule />

        {/* Expanded: children self-render inline. Collapsed: they self-render as
            menu nodes inside a single combined "Actions" dropdown. */}
        {isCollapsed ? (
          <OverflowDropdown testid={`${testid}-actions`} triggerLabel="Actions">
            {children}
          </OverflowDropdown>
        ) : (
          children
        )}
      </FabContext.Provider>
    </BuiFloatingActionBar>
  );
};

/**
 * BTFloatingActionBar with its composable sub-components attached. See
 * {@link BTFloatingActionBarInternal} for usage.
 */
export const BTFloatingActionBar = Object.assign(BTFloatingActionBarInternal, {
  /** A separator between action groups (vertical inline, horizontal in the menu). See {@link BTFloatingActionBarDivider}. */
  Divider: BTFloatingActionBarDivider,
  /** A text action with an optional leading icon. See {@link BTFloatingActionBarAction}. */
  Action: BTFloatingActionBarAction,
  /** An icon-only action (tooltip label). See {@link BTFloatingActionBarIconAction}. */
  IconAction: BTFloatingActionBarIconAction,
  /** The explicit overflow dropdown. See {@link BTFloatingActionBarMoreActions}. */
  MoreActions: BTFloatingActionBarMoreActions,
  /** An item inside the MoreActions dropdown. See {@link BTFloatingActionBarMoreActionsItem}. */
  MoreActionsItem: BTFloatingActionBarMoreActionsItem,
});
